import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';

const parseFlexDate = (val) => {
  const s = String(val || '');
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (m) return new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`).getTime();
  const t = new Date(s).getTime();
  return isNaN(t) ? Infinity : t;
};

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bumpPageReset } = useAppContext();
  const [alertBadge, setAlertBadge] = useState(0);
  const [docBadge, setDocBadge] = useState(0);

  useEffect(() => {
    const fetchCounts = () => {
      apiEndpoints.documents.list().then(res => {
        const docs = res.data || [];
        const now = Date.now();
        const thirtyDays = 30 * 24 * 60 * 60 * 1000;
        const expiring = docs.filter(d => parseFlexDate(d.expiryDate) <= now + thirtyDays);
        setAlertBadge(expiring.length);
        setDocBadge(expiring.length);
      }).catch(() => {});
    };

    fetchCounts();
    window.addEventListener('fleet:docs-changed', fetchCounts);
    return () => window.removeEventListener('fleet:docs-changed', fetchCounts);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  // Clicking the tab for the page you're already on doesn't change the URL,
  // so React Router won't reset it — force it back to its default view instead.
  const handleNavClick = (path) => {
    if (isActive(path)) bumpPageReset();
    else navigate(path);
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: 'dashboard' },
    { path: '/vehicles', label: 'Vehicles', icon: 'vehicles' },
    { path: '/alerts', label: 'Alerts', icon: 'alerts', badge: alertBadge || null },
    { path: '/companies', label: 'Companies', icon: 'companies' },
    { path: '/documents', label: 'Documents', icon: 'documents', badge: docBadge || null },
  ];

  return (
    <nav className="bottom-nav">
      {navItems.map((item) => (
        <div
          key={item.path}
          className={`bnav-item ${isActive(item.path) ? 'active' : ''}`}
          onClick={() => handleNavClick(item.path)}
        >
          <div className="bnav-icon">
            {item.icon === 'dashboard' && (
              <svg width="22" height="22" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
              </svg>
            )}
            {item.icon === 'vehicles' && (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="3" width="15" height="13" rx="2" />
                <path d="M16 8h4l3 5v3h-7V8z" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
            )}
            {item.icon === 'alerts' && (
              <svg width="22" height="22" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            {item.icon === 'companies' && (
              <svg width="22" height="22" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
            )}
            {item.icon === 'documents' && (
              <svg width="22" height="22" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          {item.badge && <span className="bnav-badge">{item.badge}</span>}
          {item.label}
        </div>
      ))}
    </nav>
  );
};

export default BottomNav;
